import {
  CaretDownOutlined,
  CaretUpOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { RadioInput } from '@mm/components';
import { Divider, InputNumber } from 'antd';
import React, { useState } from 'react';
import { PrimaryButton } from '../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { OnClickElsewhere } from '../../../../../../common/components/OnClickElsewhere/OnClickElsewhere';
import styles from './SelectAllLeadsButton.module.css';

type Props = {
  disabled: boolean;
  maxLeadsCount: number;
  count: number | undefined;
  setCount: (value: number | undefined) => void;
  onSelectLeads: (isAllSelected: boolean, leadsCount: number) => void;
  hideSelectAll?: boolean;
};

export const SelectAllLeadsButton = ({
  disabled,
  maxLeadsCount,
  count,
  setCount,
  onSelectLeads,
  hideSelectAll,
}: Props) => {
  const [openModal, setOpenModal] = useState(false);
  const defaultRadioInput = hideSelectAll ? 'quantity' : 'all';
  const [selectedOption, setSelectedOption] = useState<'all' | 'quantity'>(
    defaultRadioInput,
  );
  const [input, setInput] = useState<number | undefined>(undefined);
  const applyDisabled = selectedOption === 'quantity' && !input;

  const closeModal = () => setOpenModal(false);

  const onApplyClick = () => {
    if (selectedOption === 'all') {
      onSelectLeads(true, maxLeadsCount);
    }
    if (selectedOption === 'quantity' && input) {
      onSelectLeads(false, input);
      setCount(input);
    }
    closeModal();
  };

  const onResetAll = () => {
    onSelectLeads(false, 0);
    setSelectedOption(defaultRadioInput);
    setInput(undefined);
    closeModal();
  };

  return (
    <div className={styles.container}>
      <PrimaryButton
        disabled={disabled}
        className={styles.primaryButton}
        onClick={() => {
          setOpenModal(!openModal);
        }}
      >
        {count ? `${count.toLocaleString('en-US')} selected` : 'Select'}
        {openModal ? (
          <CaretUpOutlined className={styles.caret} />
        ) : (
          <CaretDownOutlined className={styles.caret} />
        )}
      </PrimaryButton>
      {openModal && (
        <OnClickElsewhere handleClick={closeModal}>
          <div className={styles.modalContainer}>
            {!hideSelectAll && (
              <div className={styles.radioInpuWrapper}>
                <RadioInput
                  className={styles.radioButton}
                  name="all"
                  label={`Select all ${maxLeadsCount.toLocaleString(
                    'en-US',
                  )} leads`}
                  checked={selectedOption === 'all'}
                  onChange={() => {
                    setInput(undefined);
                    setSelectedOption('all');
                  }}
                  value="all"
                />
              </div>
            )}
            <div className={styles.radioInpuWrapper}>
              <RadioInput
                className={styles.radioButton}
                name="quantity"
                label="Select number of leads"
                checked={selectedOption === 'quantity'}
                onChange={() => {
                  setCount(undefined);
                  setSelectedOption('quantity');
                }}
                value="quantity"
              />
            </div>

            <div className={styles.buttons}>
              {selectedOption === 'quantity' && (
                <InputNumber
                  min={1}
                  max={maxLeadsCount}
                  autoFocus
                  defaultValue={input}
                  value={input}
                  onChange={value => setInput(value || 1)}
                />
              )}
              <PrimaryButton
                className={styles.primaryButton}
                disabled={applyDisabled}
                onClick={onApplyClick}
              >
                Apply
              </PrimaryButton>
            </div>
            <Divider style={{ margin: '8px 0px' }} />
            <div className={styles.resetBlock} onClick={onResetAll}>
              <CloseOutlined />
              Reset all
            </div>
          </div>
        </OnClickElsewhere>
      )}
    </div>
  );
};
